import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion, Variants } from 'framer-motion';

const drawerNav: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const drawerItem: Variants = {
  hidden: { x: 24, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
};

const Header: React.FC = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = React.useRef(0);

  const triggerNavItem = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const sections = ['Main', 'About', 'Experience', 'Contact'];

  // Show header when scrolling up, hide when scrolling down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY.current || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu with Escape
  useEffect(() => {
    if (!mobileMenu) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenu(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mobileMenu]);

  // Keep the header visible while the menu is open: a transformed ancestor
  // would become the containing block for the menu's fixed-position overlay
  const headerShown = isVisible || mobileMenu;

  return (
    <motion.header
      animate={{ y: headerShown ? 0 : '-110%' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`w-full z-50 top-0 py-3 sm:py-5 fixed bg-primary ${
        headerShown ? '' : 'pointer-events-none'
      }`}
    >
      <div className="container flex items-center justify-end">
        {/* Navigation, always visible on first page, visible on hover */}
        <nav className="hidden lg:flex flex-row items-center space-x-6">
          {sections.map((section) => (
            <li className="group list-none" key={section}>
              <span
                onClick={() => triggerNavItem(`#${section.toLowerCase()}`)}
                className="cursor-pointer pt-0.5 font-header text-lg font-semibold uppercase text-white"
              >
                {section}
              </span>
              {/* Underline grows from the left on hover */}
              <span className="block h-0.5 w-full origin-left scale-x-0 bg-yellow transition-transform duration-300 group-hover:scale-x-100"></span>
            </li>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <div className="block lg:hidden">
          <motion.button
            whileTap={{ scale: 0.9 }}
            aria-expanded={mobileMenu}
            aria-label="Open menu"
            onClick={() => setMobileMenu(true)}
          >
            <i className="bx bx-menu text-4xl text-white"></i>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMobileMenu(false)}
            className="pointer-events-auto fixed inset-0 z-70 min-h-screen bg-black bg-opacity-70 lg:hidden"
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 min-h-screen w-2/3 bg-primary py-4 px-8 shadow md:w-1/3"
            >
              <motion.button
                whileTap={{ scale: 0.9 }}
                aria-label="Close menu"
                className="absolute top-0 right-0 mt-4 mr-4"
                onClick={() => setMobileMenu(false)}
              >
                <img src="/assets/img/icon-close.svg" className="h-10 w-auto" alt="Close Menu" />
              </motion.button>
              <motion.ul className="mt-8" initial="hidden" animate="show" variants={drawerNav}>
                {sections.map((section) => (
                  <motion.li className="py-2" key={section} variants={drawerItem}>
                    <span
                      onClick={() => {
                        setMobileMenu(false);
                        triggerNavItem(`#${section.toLowerCase()}`);
                      }}
                      className="cursor-pointer pt-0.5 font-header text-lg font-semibold uppercase text-white"
                    >
                      {section}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
